import React from 'react';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { Fact } from '@/types';

type ViewableFact = Pick<Fact, 'description' | 'author' | 'factDateText' | 'sourceText' | 'topic' | 'significance'>;

interface LabellerFactViewerProps {
  currentFact: ViewableFact | null | undefined;
  currentFactIndex: number;
  total: number;
  isLoading?: boolean;
  isError?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  children?: React.ReactNode;
}

export const LabellerFactViewer = ({
  currentFact,
  currentFactIndex,
  total,
  isLoading = false,
  isError = false,
  onPrevious,
  onNext,
  children,
}: LabellerFactViewerProps) => {
  return (
    <div className="w-1/2 overflow-y-auto border-r border-gray-200 p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          Fact {currentFactIndex + 1} of {total}
        </h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onPrevious} disabled={currentFactIndex === 0}>
            <Icon className="lucide--chevron-left h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={onNext} disabled={currentFactIndex === total - 1}>
            <Icon className="lucide--chevron-right h-4 w-4" />
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Icon className="lucide--loader-2 animate-spin" />
          Loading fact…
        </div>
      )}

      {!isLoading && isError && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This fact is no longer available — it may have been clustered or archived.
        </div>
      )}

      {!isLoading && !isError && currentFact && (
        <div className="space-y-4">
          {children}

          <div>
            <Label className="text-sm font-medium text-gray-600">Description</Label>
            <p className="mt-1 text-sm">{currentFact.description || 'No description'}</p>
          </div>

          {currentFact.author && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Author</Label>
              <p className="mt-1 text-sm">{currentFact.author}</p>
            </div>
          )}

          {currentFact.factDateText && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Date</Label>
              <p className="mt-1 text-sm">{currentFact.factDateText}</p>
            </div>
          )}

          {currentFact.sourceText && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Source</Label>
              <p className="mt-1 text-sm">{currentFact.sourceText}</p>
            </div>
          )}

          {currentFact.topic && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Topic</Label>
              <p className="mt-1 text-sm">{currentFact.topic}</p>
            </div>
          )}

          {currentFact.significance && (
            <div>
              <Label className="text-sm font-medium text-gray-600">Significance</Label>
              <p className="mt-1 text-sm">{currentFact.significance}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
